import { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@chakra-ui/react";
import React from "react";

/** Never let a column be dragged so narrow it stops being readable. */
const MIN_PERCENT = 4;

const readStored = (storageKey, columns) => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : null;

    // A stored layout from an older column set must be ignored, not stretched.
    if (
      Array.isArray(parsed) &&
      parsed.length === columns.length &&
      parsed.every((value) => typeof value === "number" && value > 0)
    ) {
      return parsed;
    }
  } catch {
    // A corrupt or unavailable localStorage just means the defaults are used.
  }

  return columns.map((column) => column.width);
};

/**
 * Column widths as percentages that always add up to 100, so the table fills
 * its box exactly and never scrolls sideways: a drag takes from one column and
 * gives the same amount to its neighbour.
 *
 * Widths are remembered per table in localStorage, since an operator sizes the
 * columns once for the way they read the list.
 */
export const useResizableColumns = (storageKey, columns) => {
  const [widths, setWidths] = useState(() => readStored(storageKey, columns));
  const tableRef = useRef(null);
  const drag = useRef(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(widths));
    } catch {
      // Not being able to remember the layout is not worth breaking the page.
    }
  }, [storageKey, widths]);

  const onMouseMove = useCallback((event) => {
    const current = drag.current;

    if (!current) {
      return;
    }

    const movedPercent =
      ((event.clientX - current.startX) / current.tableWidth) * 100;

    const pair = current.left + current.right;
    const left = Math.min(
      Math.max(current.left + movedPercent, MIN_PERCENT),
      pair - MIN_PERCENT
    );

    setWidths((previous) => {
      const next = [...previous];
      next[current.index] = left;
      next[current.index + 1] = pair - left;

      return next;
    });
  }, []);

  const stop = useCallback(() => {
    drag.current = null;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", stop);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", stop);
    };
  }, [onMouseMove, stop]);

  const startResize = useCallback(
    (index) => (event) => {
      // The header row is clickable in some tables; a resize is not a click.
      event.preventDefault();
      event.stopPropagation();

      const tableWidth = tableRef.current?.offsetWidth || 1;

      drag.current = {
        index,
        startX: event.clientX,
        tableWidth,
        left: widths[index],
        right: widths[index + 1],
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [widths]
  );

  const reset = useCallback(
    () => setWidths(columns.map((column) => column.width)),
    [columns]
  );

  return { widths, tableRef, startResize, reset };
};

/**
 * The grab strip on a column edge. It sits inside the header cell and is only
 * visible once the pointer is near it, so the header stays quiet.
 */
export const ResizeHandle = ({ onMouseDown }) => (
  <Box
    onMouseDown={onMouseDown}
    onClick={(event) => event.stopPropagation()}
    position="absolute"
    top={0}
    right="-3px"
    width="6px"
    height="100%"
    cursor="col-resize"
    zIndex={1}
    _hover={{ bg: "blue.300" }}
    _active={{ bg: "blue.500" }}
    borderRadius="full"
    transition="background .12s ease"
  />
);

/** Fixed layout plus a colgroup is what makes the percentages authoritative. */
export const ColumnWidths = ({ widths }) => (
  <colgroup>
    {widths.map((width, index) => (
      <col key={index} style={{ width: `${width}%` }} />
    ))}
  </colgroup>
);
